/**
 * **Der Satz zu einem Rücksprung unter „Strom & Erzeugung"** (HM-180).
 *
 * Gefunden hat ihn der Server (`SiteEnergyResponse.counterResets`); hier
 * entsteht nur der Text. Ohne ihn stand an der Stelle eine Lücke ohne Grund —
 * der Befund lag im Protokoll des Workers, wo ihn niemand liest, der vor der
 * Seite sitzt.
 *
 * Gezählt wird je **Zähler**, nicht je Zählwerk: Springen Bezug und Einspeisung
 * desselben Zählers gleichzeitig, ist das ein Ereignis und eine Frage.
 */
import type { ApiComponents } from "@hausmeter/sdk";
import { formatDateTime } from "./format";

type CounterReset = ApiComponents["schemas"]["CounterReset"];

export type CounterResetNote = {
  readonly title: string;
  readonly text: string;
  /** Der Zähler, zu dem der Knopf führt — nur, wenn es genau einer ist. */
  readonly meterId: string | null;
};

export function counterResetNote(resets: readonly CounterReset[]): CounterResetNote | null {
  const byMeter = new Map<string, CounterReset>();
  for (const reset of resets) {
    const known = byMeter.get(reset.meterId);
    if (known === undefined || reset.at < known.at) byMeter.set(reset.meterId, reset);
  }
  const first = [...byMeter.values()].sort((a, b) => a.at.localeCompare(b.at));
  if (first.length === 0) return null;

  const why =
    "Über den Sprung wird nichts gerechnet, der Verbrauch dieser Viertelstunde fehlt deshalb. " +
    "Meist wurde der Zähler getauscht, oder der Lesekopf liest gerade einen anderen Zähler. " +
    "Einen Zählerwechsel einzutragen geht hier noch nicht.";

  if (first.length === 1) {
    const reset = first[0]!;
    return {
      title: `Der Stand von „${reset.meterName}“ ist zurückgesprungen`,
      text: `Am ${formatDateTime(reset.at)} lag er unter dem der Viertelstunde davor. ${why}`,
      meterId: reset.meterId,
    };
  }
  return {
    title: `An ${first.length} Zählern ist der Stand zurückgesprungen`,
    text:
      first.map((reset) => `„${reset.meterName}“ am ${formatDateTime(reset.at)}`).join(", ") +
      `. ${why}`,
    meterId: null,
  };
}
